<template>
  <div class="config-container">
    <div class="step1-summary-bar">
      <div class="step1-summary-tags-row">
        <span class="step1-summary-label">当前配置：</span>
        <el-tag size="small" type="primary" effect="plain">
          {{ taskTypeText }}
        </el-tag>
        <el-tag size="small" type="success" effect="plain">
          {{ cycleTypeText }}
        </el-tag>
        <el-tag v-if="formData.taskYear" size="small" type="info" effect="plain">
          {{ formData.taskYear }}年
        </el-tag>
        <el-tag v-if="zoneName" size="small" type="info" effect="plain">
          {{ zoneName }}
        </el-tag>
        <el-tag v-if="showSplitMonthlySwitch && formData.isSplitMonthly" size="small" type="warning" effect="plain">
          按月分解
        </el-tag>
      </div>
      <p v-if="cycleBehaviorHint" class="step1-summary-cycle-hint">
        <i class="el-icon-info" />
        {{ cycleBehaviorHint }}
      </p>
    </div>

    <!-- 按月分解开关 -->
    <div v-if="showSplitMonthlySwitch" class="split-monthly-bar">
      <div class="split-monthly-row">
        <span class="split-monthly-label">按月分解执行</span>
        <el-switch
          v-model="formData.isSplitMonthly"
          active-text="是"
          inactive-text="否"
          @change="handleSplitMonthlyChange"
        />
        <span class="split-monthly-hint">
          开启后从当前任务年份内指定月份起拆为多个月度期次，可为各月单独配置系统与区域
        </span>
      </div>
    </div>

    <!-- 任务期次列表 -->
    <div class="config-body">
      <!-- 左侧任务期次列表，每日、专项周期不显示 -->
      <div v-if="showPeriodList" class="period-sidebar">
        <div class="sidebar-header">
          <span class="sidebar-header-title">任务期次</span>
          <el-button type="text" icon="el-icon-plus" :disabled="splitMonthlyActive" @click="addPeriod(null)">添加</el-button>
        </div>
        <div class="period-list-scroll">
          <div
            v-for="(period, index) in formData.periodConfigs"
            :key="index"
            class="period-list-item"
            :class="{ active: currentPeriodIndex === index }"
            @click="handlePeriodClick(index)"
          >
            <span class="period-name">{{ period.periodName || '未命名周期' }}</span>
            <div class="period-actions">
              <el-tooltip content="复制此周期" placement="top">
                <i v-if="!splitMonthlyActive" class="el-icon-document-copy action-icon" @click.stop="handleCopyPeriod(index)"/>
              </el-tooltip>
              <i v-if="formData.periodConfigs.length > 1" class="el-icon-close action-icon remove" @click.stop="removePeriod(index)"/>
            </div>
          </div>
        </div>
      </div>

      <!-- 右侧任务期次配置 -->
      <div class="config-main">
        <div v-if="currentPeriod" class="current-period-config">
          <div class="config-header-row">
            <el-input v-model="currentPeriod.periodName" placeholder="任务期次" style="width: 220px; margin-right: 10px;">
              <template slot="prepend">
                <span class="period-name-prepend-label">
                  任务期次
                  <el-tooltip content="任务期次即该任务的周期名称，用于实际执行中显示的任务名称。" placement="top">
                    <i class="el-icon-question period-name-tip-icon" />
                  </el-tooltip>
                </span>
              </template>
            </el-input>
            <div class="plan-date-input-group">
              <span class="plan-date-input-group__prepend">计划开始日期</span>
              <el-date-picker
                v-model="currentPeriod.executeDate"
                type="date"
                placeholder="选择日期"
                class="plan-date-picker-grouped"
              />
            </div>
            <el-date-picker
              v-model="currentPeriod.deadlineDate"
              type="date"
              placeholder="截止日期(选填)"
              style="width: 160px;"
            />
          </div>
          <div class="system-select-row">
            <label>选择消防系统：</label>
            <el-select
              :value="currentSelectedSystems"
              multiple
              collapse-tags
              placeholder="请选择本周期需要检查的系统"
              style="width: 100%"
              @change="handleSystemMultiChange"
            >
              <el-option
                v-for="item in systemList"
                :key="item.uuid"
                :label="item.systemName"
                :value="item.uuid"
              />
            </el-select>
          </div>

          <div class="system-config-list">
            <div v-if="currentPeriod.systemConfigs.length === 0" class="empty-placeholder">
              <i class="el-icon-info" />
              <p>请先在上方选择消防系统</p>
            </div>

            <div
              v-for="(sysConfig, sIndex) in currentPeriod.systemConfigs"
              :key="sIndex"
              class="system-card"
            >
              <div class="system-card-header">
                <span class="system-name">{{ getSystemName(sysConfig.systemId) }}</span>
                <el-button type="text" icon="el-icon-delete" class="danger-text" @click="removeSystemConfig(sIndex)">移除</el-button>
              </div>
              <div class="system-card-body">
                <el-form label-position="top" size="small">
                  <el-row :gutter="20">
                    <el-col :span="12">
                      <el-form-item label="设施类型">
                        <el-select
                          v-model="sysConfig.deviceTypeIds"
                          multiple
                          collapse-tags
                          placeholder="全部类型"
                          style="width: 100%"
                        >
                          <el-option
                            v-for="item in getDeviceTypes(sysConfig.systemId)"
                            :key="item.uuid"
                            :label="item.typeName"
                            :value="item.uuid"
                          />
                        </el-select>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item label="子区域范围 (默认全区)">
                        <AreaCascader
                          v-model="sysConfig.areaIds"
                          :options="currentSubAreas"
                          multiple
                          :check-strictly="false"
                          collapse-tags
                          placeholder="全区设备"
                          style="width: 100%"
                          clearable
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 第二步：按任务期次配置计划日期、消防系统、设施类型与子区域
 * 本步骤自行管理期次选中状态与操作逻辑；
 * 需要整体替换 periodConfigs 或变更 isSplitMonthly 时通过 emit 通知父组件。
 */

// 工具函数
import {
  getTaskTypeLabel,
  getCycleTypeLabel,
  isCycleWithoutTaskYear,
  isYearSplitMonthlyCycle
} from '@/features/gnmk/constants/taskCycleEnums';
import {
  getMaxExecuteDateAmongPeriods,
  getNextDate,
  generatePeriodName,
  isDateInTaskYear,
  normalizeDateToTaskYear,
  getDefaultPeriodStartDate,
  buildMonthlySplitPeriodConfigs
} from '../utils/dateCalc';
import { findAreaNodeByUuid, getZoneNameFromTree } from '../utils/areaHandler';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'Step2DetailConfig',
  components: { AreaCascader },
  props: {
    /** 含 periodConfigs[].systemConfigs 的完整表单（只读，写操作通过 emit 通知父级） */
    formData: {
      type: Object,
      required: true
    },
    /** 区域树（大区级联数据源） */
    areaTree: {
      type: Array,
      default: () => []
    },
    /** 可选消防系统列表 */
    systemList: {
      type: Array,
      default: () => []
    },
    /** 设施类型全集，按 systemId 过滤使用 */
    typeList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      /** 当前选中的期次下标，纯 UI 状态 */
      currentPeriodIndex: 0,
      /** 当前期次已选系统 uuid，与 el-select 多选受控绑定 */
      currentSelectedSystems: []
    };
  },
  computed: {
    /** 任务类型文本 */
    taskTypeText () {
      return getTaskTypeLabel(this.formData.taskType);
    },
    /** 执行周期文本 */
    cycleTypeText () {
      return getCycleTypeLabel(this.formData.cycleType);
    },
    /** 是否显示按月分解开关 */
    showSplitMonthlySwitch () {
      return isYearSplitMonthlyCycle(this.formData.cycleType);
    },
    /** 按月分解开关是否激活 */
    splitMonthlyActive () {
      return this.showSplitMonthlySwitch && this.formData.isSplitMonthly;
    },
    /** 每日、专项周期不显示任务期次列表 */
    showPeriodList () {
      return !isCycleWithoutTaskYear(this.formData.cycleType);
    },
    /** 当前大区节点作为级联根，子区域范围仅在该区内选 */
    currentSubAreas () {
      if (!this.formData.zoneAreaId) return [];
      const zone = findAreaNodeByUuid(this.areaTree, this.formData.zoneAreaId);
      return zone ? [zone] : [];
    },
    /** 当前大区名称，摘要条展示用 */
    zoneName () {
      return getZoneNameFromTree(this.areaTree, this.formData.zoneAreaId);
    },
    /** 当前编辑中的期次配置 */
    currentPeriod () {
      return this.formData.periodConfigs[this.currentPeriodIndex] || null;
    },
    /** 第一步执行周期为专项/日常时在摘要条补充说明 */
    cycleBehaviorHint () {
      const cycle = this.formData && this.formData.cycleType;
      if (cycle === 'special') {
        return '专项任务是一次性任务，不会像每日任务一样自动生成。';
      }
      if (cycle === 'day') {
        return '日常任务会每日 0 点定时生成每一天相同配置的任务。';
      }
      return '';
    }
  },
  watch: {
    /** 切换期次时同步受控多选值 */
    currentPeriodIndex () {
      this.syncSelectedSystems();
    },
    /**
     * 父组件重置或切换周期类型后 periodConfigs 会整体替换，
     * 需要将本地选中下标归零并同步多选受控值。
     */
    'formData.periodConfigs' () {
      this.currentPeriodIndex = 0;
      this.syncSelectedSystems();
    }
  },
  methods: {
    /** 切换当前编辑期次 */
    handlePeriodClick (index) {
      this.currentPeriodIndex = index;
    },

    /** 从当前期次的 systemConfigs 同步多选消防系统的受控值 */
    syncSelectedSystems () {
      if (this.currentPeriod) {
        this.currentSelectedSystems = this.currentPeriod.systemConfigs.map(c => c.systemId);
      } else {
        this.currentSelectedSystems = [];
      }
    },

    /** 当前周期类型与任务年份下的默认计划开始日期 */
    getDefaultPeriodStartDate () {
      return getDefaultPeriodStartDate(this.formData.cycleType, this.formData.taskYear);
    },

    /**
     * 新增一个期次：按月分解模式下禁止手动添加；
     * 否则根据基准日期或上一期最大执行日推算下一期，并校验落在任务年内。
     * 直接 push 到 periodConfigs 引用（Vue2 响应式追踪数组变更）。
     * @param {Date|string|null} baseDate 指定的计划开始日，不传则按规则推算
     */
    addPeriod (baseDate = null) {
      if (this.splitMonthlyActive) {
        this.$message.warning('已开启按月分解，期次由系统自动排布，无需手动添加');
        return;
      }
      let targetDate = new Date();
      let fromLastPeriod = false;
      if (baseDate) {
        targetDate = new Date(baseDate);
      } else if (this.formData.periodConfigs.length > 0) {
        const maxDate = getMaxExecuteDateAmongPeriods(this.formData.periodConfigs);
        if (maxDate) {
          targetDate = getNextDate(maxDate, this.formData.cycleType);
          fromLastPeriod = true;
        }
      } else {
        targetDate = this.getDefaultPeriodStartDate();
      }
      if (!fromLastPeriod) {
        targetDate = normalizeDateToTaskYear(targetDate, this.formData.cycleType, this.formData.taskYear);
      }

      if (!isDateInTaskYear(targetDate, this.formData.cycleType, this.formData.taskYear)) {
        this.$message.warning(`任务周期不能超出${this.formData.taskYear}年范围`);
        return;
      }

      const name = generatePeriodName(targetDate, this.formData.cycleType);
      this.formData.periodConfigs.push({
        periodName: name,
        executeDate: targetDate,
        deadlineDate: null,
        systemConfigs: []
      });
    },

    /**
     * 复制指定期次：系统配置深拷贝，计划日为全局最后一期的下一周期日。
     * @param {number} index 被复制的期次下标
     */
    handleCopyPeriod (index) {
      if (this.splitMonthlyActive) {
        this.$message.warning('已开启按月分解，请关闭后再使用复制');
        return;
      }
      const source = this.formData.periodConfigs[index];
      const base = getMaxExecuteDateAmongPeriods(this.formData.periodConfigs);
      if (!base) {
        this.$message.warning('请先为周期设置计划开始日期');
        return;
      }
      const nextDate = getNextDate(base, this.formData.cycleType);
      if (!isDateInTaskYear(nextDate, this.formData.cycleType, this.formData.taskYear)) {
        this.$message.warning(`复制后的任务周期不能超出${this.formData.taskYear}年范围`);
        return;
      }
      const newConfig = {
        periodName: generatePeriodName(nextDate, this.formData.cycleType),
        executeDate: nextDate,
        deadlineDate: null,
        systemConfigs: JSON.parse(JSON.stringify(source.systemConfigs || []))
      };
      this.formData.periodConfigs.splice(index + 1, 0, newConfig);
      this.currentPeriodIndex = index + 1;
      this.syncSelectedSystems();
    },

    /** 删除指定期次并修正当前选中下标 */
    removePeriod (index) {
      this.formData.periodConfigs.splice(index, 1);
      if (this.currentPeriodIndex >= this.formData.periodConfigs.length) {
        this.currentPeriodIndex = Math.max(0, this.formData.periodConfigs.length - 1);
      }
    },

    /**
     * 消防系统多选变更：增删 systemConfigs 项，保证与下拉选中项一致。
     * @param {string[]} val 选中的系统 uuid 列表
     */
    handleSystemMultiChange (val) {
      if (!this.currentPeriod) return;
      const selected = Array.isArray(val) ? val : [];
      const currentConfigs = this.currentPeriod.systemConfigs;

      for (let i = currentConfigs.length - 1; i >= 0; i--) {
        if (!selected.includes(currentConfigs[i].systemId)) {
          currentConfigs.splice(i, 1);
        }
      }
      selected.forEach(sysId => {
        if (!currentConfigs.find(c => c.systemId === sysId)) {
          currentConfigs.push({ systemId: sysId, deviceTypeIds: [], areaIds: [] });
        }
      });
      // 子组件用 :value 受控绑定，须与 systemConfigs 同步，否则下拉无法保持多选状态
      this.syncSelectedSystems();
    },

    /** 从当前期次移除某一消防系统及其设施类型、子区域配置 */
    removeSystemConfig (index) {
      this.currentPeriod.systemConfigs.splice(index, 1);
      this.syncSelectedSystems();
    },

    /**
     * 按月分解开关：开启则通知父组件生成 12 个月度期次，关闭则通知父组件恢复单期默认。
     * formData.isSplitMonthly 已由 v-model 直接更新，此处只处理 periodConfigs 的重建。
     */
    handleSplitMonthlyChange (val) {
      if (!this.showSplitMonthlySwitch) return;
      if (val) {
        const list = buildMonthlySplitPeriodConfigs(this.formData.taskYear);
        if (list) {
          this.$emit('update:period-configs', list);
        }
      } else {
        this.$emit('reset-to-single-period');
      }
    },

    /** 根据系统 uuid 取展示名 */
    getSystemName (id) {
      const sys = this.systemList.find(s => s.uuid === id);
      return sys ? sys.systemName : '未知系统';
    },

    /** 某消防系统下可选的设施类型列表 */
    getDeviceTypes (systemId) {
      if (!systemId) return [];
      return this.typeList.filter(t => t.systemId === systemId);
    },

    /**
     * 供父组件在重置/周期类型变更后调用，将期次列表归零并重置选中下标。
     * 使用 ref 调用而非 prop/emit 以保证时序：父组件先更新 formData 再调用此方法。
     */
    resetPeriodIndex () {
      this.currentPeriodIndex = 0;
      this.syncSelectedSystems();
    }
  }
};
</script>

<style scoped lang="scss">
.config-container {
  display: flex;
  flex-direction: column;
  height: 500px;
  border: 1px solid #ebeef5;
  border-radius: 4px;

  .period-name-tip-icon {
    color: #909399;
    font-size: 14px;
    cursor: help;
    font-weight: normal;
  }

  .step1-summary-bar {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    padding: 8px 15px;
    background: #f0f9ff;
    border-bottom: 1px solid #d0e8f7;
    flex-shrink: 0;

    .step1-summary-tags-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
    }

    .step1-summary-label {
      font-size: 13px;
      color: #606266;
      white-space: nowrap;
    }

    .step1-summary-cycle-hint {
      margin: 0;
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-size: 12px;
      color: #606266;
      line-height: 1.5;

      i {
        flex-shrink: 0;
        margin-top: 2px;
        color: #409eff;
        font-size: 14px;
      }
    }
  }

  .split-monthly-bar {
    flex-shrink: 0;
    padding: 10px 15px;
    background: #fdf6ec;
    border-bottom: 1px solid #faecd8;

    .split-monthly-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px 12px;
    }

    .split-monthly-label {
      font-size: 13px;
      color: #606266;
      font-weight: 500;
      white-space: nowrap;
    }

    .split-monthly-hint {
      color: #909399;
      font-size: 12px;
      line-height: 1.5;
      flex: 1;
      min-width: 220px;
    }
  }

  .config-body {
    display: flex;
    flex: 1;
    overflow: hidden;
  }

  .period-sidebar {
    width: 200px;
    border-right: 1px solid #ebeef5;
    background: #fcfcfc;
    display: flex;
    flex-direction: column;

    .sidebar-header {
      padding: 10px 15px;
      border-bottom: 1px solid #ebeef5;
      font-weight: bold;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f5f7fa;

      .sidebar-header-title {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
    }

    .period-list-scroll {
      flex: 1;
      overflow-y: auto;
    }

    .period-list-item {
      padding: 12px 15px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;

      &:hover {
        background: #f5f7fa;
      }

      &.active {
        background: #ecf5ff;
        color: #409EFF;
        border-right: 2px solid #409EFF;
      }

      .period-name {
        font-size: 13px;
      }

      .period-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .action-icon {
        color: #C0C4CC;
        font-size: 14px;
        padding: 4px;

        &:hover {
          color: #409EFF;
          background: #e6f1fc;
          border-radius: 4px;
        }

        &.remove:hover {
          color: #F56C6C;
          background: #fef0f0;
        }
      }
    }
  }

  .config-main {
    flex: 1;
    padding: 15px;
    overflow-y: auto;

    .config-header-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 15px;
      padding-bottom: 15px;
      border-bottom: 1px dashed #ebeef5;

      .period-name-prepend-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }

      .plan-date-input-group {
        display: inline-flex;
        flex-wrap: nowrap;
        align-items: stretch;
        vertical-align: middle;
        margin-right: 10px;
        max-width: 100%;

        &__prepend {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          padding: 0 12px;
          font-size: 13px;
          color: #909399;
          background-color: #f5f7fa;
          border: 1px solid #dcdfe6;
          border-right: 0;
          border-radius: 4px 0 0 4px;
          box-sizing: border-box;
          line-height: 1;
          white-space: nowrap;
        }

        .plan-date-picker-grouped {
          width: 168px;
          flex: 0 0 auto;

          ::v-deep .el-date-editor.el-input {
            width: 100%;
          }

          ::v-deep .el-input__inner {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
          }
        }
      }
    }

    .system-select-row {
      margin-bottom: 15px;
      display: flex;
      align-items: center;
      label { margin-right: 10px; width: 130px; }
    }

    .system-config-list .empty-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      color: #909399;
      font-size: 13px;
      padding: 5px 0;

      i {
        font-size: 14px;
      }

      p {
        margin: 0;
      }
    }

    .system-card {
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      margin-bottom: 15px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);

      .system-card-header {
        background: #f5f7fa;
        padding: 8px 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #ebeef5;

        .system-name { font-weight: bold; font-size: 14px; }
      }

      .system-card-body {
        padding: 15px;
      }
    }
  }
}

.danger-text { color: #F56C6C; }
</style>
